import { Case } from './../model/case';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CompositeFilterDescriptor, GroupResult, groupBy } from '@progress/kendo-data-query';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { FilterService } from '../../core/filter/filter.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { CaseListService } from '../../core/service/grid-data/grid-data-case.service';
import { CaseUtility } from '../case.utility';
import { State } from '@progress/kendo-data-query';
import
{
  GridDataResult, DataStateChangeEvent, SelectAllCheckboxState, ColumnVisibilityChangeEvent, ColumnReorderEvent,
  GridComponent
} from '@progress/kendo-angular-grid';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { Filter } from 'src/app/core/filter/filter';
import { ChannelService } from 'src/app/core/service/channel.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { CaseService } from 'src/app/core/service/case.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { ColumnSettings } from 'src/app/core/model/column-settings';
import { cloneDeep, sortBy } from 'lodash';
import { GridResizeEvent } from 'src/app/core/model/grid-settings';
import { UserService } from 'src/app/core/service/user.service';
import { User } from 'src/app/user/model/user';

@Component({
  standalone: false,
  selector: 'app-case-list',
  templateUrl: './case-list.component.html',
  styleUrls: ['./case-list.component.scss']
})
export class CaseListComponent implements OnInit, OnDestroy
{
  @ViewChild(GridComponent) public grid: GridComponent;
  @ViewChild('enrollmentReason') public enrollmentReasonControl: any;
  @ViewChild('manager') public managerControl: any;

  public autoFitColumnsEnabled = false;
  public isRestrictedPaging = false;
  public pageSizes = [5, 10, 25, 50, 100, 1000];
  public enableAdvanced = false;
  public currentUser = this.authenticationService.currentUser;
  public breadcrumb = 'ALL';
  public stringLiteralBreadcrumb = false;
  public filterDialogOpened = false;
  public assigneeDialogOpened = false;
  public patientLabel = {};
  public closeCasesDialogOpened = false;
  public reassignCasesDialogOpened = false;
  public selectedCaseKeys: number[] = [];
  public selectAllState: SelectAllCheckboxState = 'unchecked';
  public workflowStatuses: any = [];
  public channelsProcessedReassign: any = [];
  public defaultGridState = null;
  public defaultColumnsConfig: ColumnSettings[];
  public columnsConfig: ColumnSettings[];
  public isRecentViewContext = false;
  public searchTerms = '';
  public caseManagers: User[] = [];
  public caseManagersFiltered: User[] = [];

  /**
   * The reason for closing selected cases.
   */
  public reasonForClose = null;
  public reasonForReassign = null;
  public reassignOpenTasksNgModel = null;
  public enrollForm: any;
  public channels: any[];
  public groupedChannels: GroupResult[] = [];
  public communityEnabled = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;

  /**
   * The selected case.
   */
  public aCase: any;

  /**
   * Case list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public filterText = '';
  public filterSelected: Filter;
  public distinctPriorities = this.caseUtilityService.distinctPriorities;
  public distinctStatus = this.caseUtilityService.distinctStatus;
  public searchControl = new UntypedFormControl();

  /**
   * Subscription to Angular component route parameters.
   */
  public routeParamSubscription: Subscription;

  private messagingCaseManagerUpdatedSubscription: Subscription;
  private listSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private filterService: FilterService,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private caseListService: CaseListService,
    private messengerService: MessengerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private caseUtilityService: CaseUtility,
    private settingService: SettingService,
    private channelService: ChannelService,
    private caseService: CaseService,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService,
    public intl: IntlService,
    private userService: UserService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.caseListService.loading;
  }

  public get channelToEnroll(): UntypedFormControl
  {
    return this.enrollForm.get('channelToEnroll');
  }

  public get enrollmentReason(): UntypedFormControl
  {
    return this.enrollForm.get('enrollmentReason');
  }

  public get manager(): UntypedFormControl
  {
    return this.enrollForm.get('manager');
  }

  public get includeCaseExtensions(): UntypedFormControl
  {
    return this.enrollForm.get('includeCaseExtensions');
  }

  public ngOnInit(): void
  {
    this.searchControl.valueChanges
      .pipe(debounceTime(500))
      .subscribe({
        next: (searchValue: string) =>
        {
          this.searchFieldChange(searchValue);
        }
      });

    this.listSubscription = this.caseListService.subscribe({
      next: (value) =>
      {
        // Preprocessing data before it's passed to the Kendo Grid.
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.priorityBadgeClass = this.caseUtilityService.priorityBadgeClass(d.priority);
            d.priorityLetter = this.caseUtilityService.priorityLetter(d.priority);
          });
        }
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.total = value.total : this.total = 0
        };
        this.autoFitColumns();
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.isRestrictedPaging = sessionSetting.pagingRestrictionCaseEnabled &&
          (this.currentUser.role === 'ROLE_PROGRAM_LEAD' || this.currentUser.role === 'ROLE_CARE_COORDINATOR' || this.currentUser.role === 'ROLE_COMMUNITY_COORDINATOR');
        this.autoFitColumnsEnabled = sessionSetting.autofitGridColumnsCase;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.communityEnabled = sessionSetting.communityEnabled;

        this.filterService.getFilter(sessionSetting.defaultGridFilterCase).subscribe({
          next: (data: Filter) =>
          {
            this.caseListService.setDefaultColumnsSetting(data?.state ? data.state : '', data?.columns ? data.columns : '',
              this.appConfigService.getConfig().pageSize, sessionSetting.patientLabel,
              this.communityEnabled).then((config: any) =>
              {
                // Keeping default grid state and column config
                this.defaultGridState = cloneDeep(config.state);
                this.defaultColumnsConfig = cloneDeep(config.columns);

                // Current grid state and column config
                this.state = cloneDeep(config.state);
                this.columnsConfig = cloneDeep(config.columns);

                // fetch cases
                this.routeParamSubscription = this.route.params.subscribe({
                  next: (params) =>
                  {
                    this.filterSelected = undefined;
                    this.getCases(params.filter);
                  }
                });
              });
          }
        });
      }
    });

    // subscribe to case manager update messages.
    this.messagingCaseManagerUpdatedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'caseManagerUpdated')
        {
          if (message.data && message.data.caseId > 0 && message.data.name)
          {
            // Manually find the row in the grid and update
            const index = this.gridView.data.findIndex(x => x.id === message.data.caseId);
            if (index >= 0)
            {
              this.gridView.data[index].manager = message.data.name;
              // If case manager is updated and no longer current logged in user, just reload
              if (this.breadcrumb === 'MY-OPEN-CASES' && this.authenticationService.currentUser.name !== message.data.name)
              {
                this.getCases('my');
              }
            }
          }
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.messagingCaseManagerUpdatedSubscription !== undefined) { this.messagingCaseManagerUpdatedSubscription.unsubscribe(); }
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.queryCases(state);
  }

  /**
   * Assign a new case manager.
   *
   * @param aCase The case to be reassigned.
   */
  public assignCaseManager(aCase: Case): void
  {
    this.aCase = aCase;
    this.assigneeDialogOpened = true;
  }

  public allAssigneesSelected(): boolean
  {
    let allAssigned = true;
    this.channelsProcessedReassign.forEach(x =>
    {
      if ((x.channelAssigneeId === null || x.channelAssigneeId === undefined) && x.channelStaff.length > 0)
      {
        allAssigned = false;
      }
    });
    return allAssigned;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.queryCases(this.state);
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const isNumericCaseNumber = /^-?\d+$/.test(filterText);
    const field = isNumericCaseNumber ? 'id' : 'name';
    const operator = isNumericCaseNumber ? 'eq' : 'contains';
    const filterValue = isNumericCaseNumber ? parseInt(filterText, 10) : filterText;
    // Clear any preceeding id or name filter
    if (this.state.filter && this.state.filter.filters)
    {
      let index = this.state.filter.filters.findIndex((obj: any) => obj.field === 'id');
      if (index !== -1)
      {
        this.state.filter.filters.splice(index, 1);
      }
      index = this.state.filter.filters.findIndex((obj: any) => obj.field === 'name');
      if (index !== -1)
      {
        this.state.filter.filters.splice(index, 1);
      }
    }
    const filters = this.searchHelperService.fieldChange(filterValue, this.state, field, operator);
    this.filterText = filterText;
    this.filterChange(filters);
  }

  /**
   * Selected cases.
   */
  public onSelectedKeysChange(e: any): void
  {
    this.selectedCaseKeys = e;
    if (this.selectedCaseKeys.length === 0)
    {
      this.selectAllState = 'unchecked';
    }
    else if (this.selectedCaseKeys.length > 0 &&
      this.selectedCaseKeys.length < this.gridView.data.filter((item) => item.status === 'Open').length)
    {
      this.selectAllState = 'indeterminate';
    }
    else
    {
      this.selectAllState = 'checked';
    }
  }

  public onSelectAllChange(checkedState: SelectAllCheckboxState)
  {
    if (checkedState === 'checked')
    {
      this.selectedCaseKeys = this.gridView.data.filter((item) => item.status === 'Open').map((item) => item.id);
      this.selectAllState = 'checked';
    }
    else
    {
      this.clearSelectedCases();
    }
  }

  /**
   * Provides the cases selected count. This controls the 'Select' button state and count display.
   *
   * @returns Number Total count of the selected providers.
   */
  public getSelectedCount(): number
  {
    return this.selectedCaseKeys.length;
  }

  public openCloseCasesDialogOpened(): void
  {
    this.channelService.getCacheMinimalChannels('?nonPrivateOnly=true').subscribe({
      next: (channels) =>
      {
        // Get channels list for enrollment option
        this.channels = channels;
        this.setGroupedChannels();

        // Initialize variables for enrollment
        this.enrollForm = this.fb.group({
          channelToEnroll: [{ value: null, disabled: false }],
          enrollmentReason: [{ value: null, disabled: false }, { validators: [Validators.required] }],
          manager: [{ value: null, disabled: false }],
          includeCaseExtensions: [{ value: true, disabled: false }],
        });

        // Set the proper validator for the manager
        this.enrollForm.get('channelToEnroll').valueChanges.subscribe({
          next: () =>
          {
            const managerValidator = [];
            if (this.channelToEnroll.value && this.channelToEnroll.value.id > 0 && this.channelToEnroll.value.caseManagerAssignmentMode === null)
            {
              managerValidator.push(Validators.required);
            }
            this.manager.setValidators(managerValidator);
            this.manager.updateValueAndValidity();

            // Load users for the Channel
            if (this.channelToEnroll.value && this.channelToEnroll.value.id > 0)
            {
              this.getUsers(this.channelToEnroll.value.id);
            }
            else
            {
              this.initManagerLists([]);
            }
          }
        });

        let caseCount = 0;
        const channelsProcessed = [];
        this.workflowStatuses = [];
        this.selectedCaseKeys.forEach((caseId) =>
        {
          this.caseService.getCaseDetails(caseId, 'SUMMARY').subscribe({
            next: (caseData) =>
            {
              if (caseData.channelCategoryWorkflowStatusId !== null &&
                channelsProcessed.findIndex(x => x === caseData.channelId) === -1)
              {
                this.dictionaryService.getCodesByCodeSetId(caseData.channelCategoryWorkflowStatusId).subscribe({
                  next: (codes) =>
                  {
                    const obj: any = {};
                    obj.channel = caseData.channel;
                    obj.program = caseData.program;
                    obj.channelId = caseData.channelId;
                    obj.workflowStatuses = codes;
                    obj.channelCategoryWorkflowStatusId = caseData.channelCategoryWorkflowStatusId;
                    obj.channelWorkflowStatusId = null;   // Will be the new workflow status id
                    this.workflowStatuses.push(obj);
                  }
                });
                channelsProcessed.push(caseData.channelId);
              }

              caseCount += 1;

              // Open dialog only if all cases are read
              if (caseCount === this.selectedCaseKeys.length)
              {
                this.closeCasesDialogOpened = true;
              }
            }
          });
        });
      }
    });
  }

  public openReassignCasesDialogOpened(): void
  {
    let caseCount = 0;
    this.channelsProcessedReassign = [];
    this.selectedCaseKeys.forEach((caseId) =>
    {
      this.caseService.getCaseDetails(caseId, 'SUMMARY').subscribe({
        next: (caseData) =>
        {
          if (this.channelsProcessedReassign.findIndex(x => x.channelId === caseData.channelId) === -1)
          {
            const obj: any = {};
            obj.channelId = caseData.channelId;
            obj.channel = caseData.channel;
            // get channel staff group
            this.userService.getUsersByCase(caseData.id).subscribe({
              next: (users) =>
              {
                obj.channelStaff = users;
              }
            });
            obj.channelAssigneeId = null;   // Will be the new assignee id
            obj.program = caseData.program;
            this.channelsProcessedReassign.push(obj);
          }

          caseCount += 1;

          // Open dialog only if all cases are read
          if (caseCount === this.selectedCaseKeys.length)
          {
            this.reassignOpenTasksNgModel = true;
            this.reassignCasesDialogOpened = true;
          }
        }
      });
    });
  }

  /**
   * Event occurs when a new workflow status is being selected for a Channel. This will update all other Channels using the same category.
   *
   * @param selectedWorkflowStatusId Selected workflow status id
   * @param index                    Current selected index
   */
  public channelWorkflowStatusIdChange(selectedWorkflowStatusId: any, index: number): void
  {
    if (selectedWorkflowStatusId !== undefined)
    {
      const otherChannels = this.workflowStatuses.filter(x =>
        x.channelCategoryWorkflowStatusId === this.workflowStatuses[index].channelCategoryWorkflowStatusId
        && !x.channelWorkflowStatusId && x.channelWorkflowStatusId !== this.workflowStatuses[index].channelWorkflowStatusId);

      // Set other channels automatically if any
      otherChannels.forEach(x =>
      {
        x.channelWorkflowStatusId = selectedWorkflowStatusId;
      });
    }
  }

  public cancelCloseCases(): void
  {
    this.closeCasesDialogOpened = false;
    this.reasonForClose = null;
  }

  public cancelReassignCases(): void
  {
    this.reassignCasesDialogOpened = false;
    this.reasonForReassign = null;
    this.reassignOpenTasksNgModel = null;
  }

  /**
   * Change the status of the case to "Closed".
   */
  public closeCases(): void
  {
    const cases = {
      caseIds: this.selectedCaseKeys,
      reason: this.reasonForClose,
      channelCategoryWorkflowStatuses: [],
      caseEnrollment: null    // Optional: Enroll closing case into another Channel
    };

    // Set channels enrollment properties
    if (this.channelToEnroll.value) 
    {
      cases.caseEnrollment = {
        channelId: this.channelToEnroll.value.id,
        enrollmentReason: this.enrollmentReason.value,
        managerId: this.manager && this.manager.value && this.manager.value.id ? this.manager.value.id : null,
        includeCaseExtensions: this.includeCaseExtensions.value
      }

      // Validations if enrolling to another case
      if (!this.enrollmentReason.value || this.enrollmentReason.value === '')
      {
        this.messageHandlerService.displayError('REQUIRED-ENROLLMENT-REASON');
        this.enrollmentReasonControl.nativeElement.focus();
        return;
      }

      if (this.channelToEnroll.value && this.channelToEnroll.value.id > 0 && this.channelToEnroll.value.caseManagerAssignmentMode === null &&
        !this.manager.value)
      {
        this.messageHandlerService.displayError('REQUIRED-MANAGER');
        this.managerControl.focus();
        return;
      }
    }

    if (this.workflowStatuses.length > 0)
    {
      cases.channelCategoryWorkflowStatuses = this.workflowStatuses.map((x: any) => ({
        channelId: x.channelId,
        channelWorkflowStatusId: x.channelWorkflowStatusId
      }));
    }

    this.caseService.closeCases(cases).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CASES-CLOSED-SUCCESSFULLY');
        this.clearSelectedCases();
        this.state.skip = 0;
        this.queryCases(this.state);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.closeCasesDialogOpened = false;
      },
      complete: () =>
      {
        this.closeCasesDialogOpened = false;
      }
    });
  }

  public reassignCases(): void
  {
    const cases = {
      caseIds: this.selectedCaseKeys,
      reason: this.reasonForReassign,
      channelCategoryAssignees: [],
      reassignOpenTasks: this.reassignOpenTasksNgModel
    };

    if (this.channelsProcessedReassign.length > 0)
    {
      cases.channelCategoryAssignees = this.channelsProcessedReassign.map((x: any) => ({
        channelId: x.channelId,
        channelAssigneeId: x.channelAssigneeId
      }));
    }

    this.caseService.reassignCases(cases).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CASES-REASSIGNED-SUCCESSFULLY');
        this.clearSelectedCases();
        this.state.skip = 0;
        this.queryCases(this.state);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.reassignCasesDialogOpened = false;
      },
      complete: () =>
      {
        this.reassignCasesDialogOpened = false;
      }
    });
  }

  public toggleAdvanced(mode: boolean): void
  {
    this.enableAdvanced = mode;
    if (mode)
    {
      this.clearSelectedCases();
    }
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
  }

  /**
   * Grid onResize event.
   *
   * @param event Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
  }

  /**
   * Reorder grid column order event. -1 is to account for kendo-grid-checkbox-column
   *
   * @param e ColumnReorderEvent event
   */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.columnsConfig.splice(e.oldIndex - 1, 1);
    this.columnsConfig.splice(e.newIndex - 1, 0, ...reorderedColumn);
  }

  public pageChange(event: any): void
  {
    this.state.take = event.take;
  }

  /**
   * Reset grid columns with default state
   */
  public resetGridColumns(): void
  {
    this.columnsConfig = cloneDeep(this.defaultColumnsConfig);
    this.state.take = this.defaultGridState.take;     // Reset just the pagesize from default grid state
    this.refresh();
  }

  public refresh(): void
  {
    this.state.skip = 0;
    this.queryCases(this.state);
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public channelItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.status !== 'Online';
  }

  /**
   * Filters the channel staff based on user input.
   */
  public handleCaseManagerFilter(filter: string): void
  {
    this.searchTerms = filter;
    this.caseManagersFiltered = this.caseManagers
      .filter(manager => manager.name.toLowerCase().includes(filter.toLowerCase()));
  }

  private initManagerLists(value)
  {
    const sorted = sortBy(value, ['name']);
    this.caseManagers = sorted;
    this.caseManagersFiltered = sorted;
  }

  private getUsers(channelId: number): void
  {
    this.userService.getUsersByChannelGroups(channelId).subscribe({
      next: (data) =>
      {
        this.initManagerLists(data);
      }
    });
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    this.channels.forEach(c => { c.programName = c.program.name; });
    const groupedChannels = groupBy(this.channels, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }

  /**
   * Get cases from API per the applied filter.
   *
   * @param filter Case filter string.
   */
  private getCases(filter: string): void
  {
    const channelId = this.route.snapshot.queryParamMap.get('channel');
    this.isRecentViewContext = filter === 'recent';

    if (filter === 'my' || filter === 'all' || filter === 'recent' || filter === 'active')
    {
      this.columnsConfig = cloneDeep(this.defaultColumnsConfig);
      this.state = cloneDeep(this.defaultGridState);

      // Must not use sort to make sure it is sorted by recent
      if (this.isRecentViewContext && this.state.sort) { this.state.sort = []; }

      this.breadcrumb = filter === 'active' ? 'ACTIVE' : filter === 'my' ? 'MY-OPEN-CASES' : 'ALL';
      this.stringLiteralBreadcrumb = false;

      if (filter === 'active')
      {
        this.state.filter = {
          filters: [
            { field: 'status', operator: 'eq', value: 'Open' },
          ], logic: 'and'
        };
      }
      else
        if (filter === 'my')
        {
          // hardcoded mechanism for 'my' cases to pick current user's name as manager.
          this.state.filter = {
            filters: [
              { field: 'manager', operator: 'eq', value: this.currentUser.name },
              { field: 'status', operator: 'contains', value: 'Open' }
            ], logic: 'and'
          };
        }
        else
        {
          delete this.state.filter;
        }

      // Link from Dashboard
      if (channelId)
      {
        this.channelService.getChannel(Number(channelId)).subscribe({
          next: (channel) =>
          {
            const allAssignee = this.route.snapshot.queryParamMap.get('allAssignee');
            const closedCases = this.route.snapshot.queryParamMap.get('closedCases');

            // AllAssignee is true when called from the Program List, therefore the manager is filtered only from the Dashboard
            if (allAssignee === 'true')
            {
              this.state.filter = {
                filters: [
                  { field: 'channel', operator: 'eq', value: channel.name },
                  { field: 'program', operator: 'eq', value: channel.program.name }
                ], logic: 'and'
              };
            }
            else
            {
              this.state.filter = {
                filters: [
                  { field: 'channel', operator: 'eq', value: channel.name },
                  { field: 'manager', operator: 'eq', value: this.currentUser.name },
                  { field: 'program', operator: 'eq', value: channel.program.name }
                ], logic: 'and'
              };
            }

            if (closedCases === 'true')
            {
              this.state.filter.filters.push({ field: 'status', operator: 'contains', value: 'Closed' });
            }
            else
            {
              this.state.filter.filters.push({ field: 'status', operator: 'contains', value: 'Open' });
            }
            this.queryCases(this.state);
            this.clearSelectedCases();
          }
        });
      }
      else
      {
        this.queryCases(this.state);
        this.clearSelectedCases();
      }
    }
    else
    {
      this.filterService.getCachedCaseFilters(this.currentUser.userName).subscribe({
        next: (filterList) =>
        {
          const item = filterList.find(obj => obj.id.toString() === filter.toString());
          this.filterSelected = item;
          this.breadcrumb = item.name;
          this.stringLiteralBreadcrumb = true;

          this.state = JSON.parse(item.state);
          this.columnsConfig = item.columns ? JSON.parse(item.columns) : this.defaultColumnsConfig;

          // reset pagination numbers as we're more concerned with filters and sorting.
          this.state.skip = 0;
          this.queryCases(this.state);
          this.clearSelectedCases();
        }
      });
    }
  }

  private clearSelectedCases(): void
  {
    this.reasonForClose = null;
    this.reasonForReassign = null;
    this.reassignOpenTasksNgModel = null;
    this.selectedCaseKeys = [];
    this.selectAllState = 'unchecked';
  }

  private queryCases(state: State): void
  {
    let queryString = this.isRecentViewContext ? '&recentOnly=true' : '';
    if (this.isRestrictedPaging) { queryString += '&restrictedPaging=true'; }
    this.caseListService.queryByState(state, null, queryString);
  }

  private autoFitColumns(): void
  {
    if (this.grid !== undefined && this.autoFitColumnsEnabled)
    {
      setTimeout(() =>
      {
        this.grid.autoFitColumns();
        this.grid.scrollTo({ row: 0, column: 0 });
      });
    }
  }
}
